<div class="pdf-document">

  <!-- ══════════════════════════════════════════════
       PAGE HEADER
  ══════════════════════════════════════════════ -->
  <div class="doc-header">
    <span class="doc-title">Amplify | {{ space.name }}</span>
    <span class="doc-date">{{ exportDate }}, {{ exportTime }}</span>
  </div>

  <!-- ══════════════════════════════════════════════
       NOTES
  ══════════════════════════════════════════════ -->
  @if (hasNotes) {
    <section class="pdf-section notes-section">
      <h2 class="section-heading">{{ 'NOTES.TITLE' | translate }}</h2>
      @for (note of space.notes; track $index) {
        <p class="note-text">{{ note.text }}</p>
        @if (!$last) {
          <hr class="note-rule">
        }
      }
    </section>
  }

  <!-- ══════════════════════════════════════════════
       COMPANY PROFILE
  ══════════════════════════════════════════════ -->
  <section class="pdf-section page-break-before">
    <h2 class="section-heading">{{ 'SECTIONS.COMPANY_PROFILE' | translate }}</h2>

    <!-- Industry -->
    @if (space.relevantIndustries.length > 0) {
      <div class="filter-group">
        <h4>{{ 'PDF.COMPANY_PROFILE.INDUSTRY' | translate }}</h4>
        <div class="industry-cards">
          @for (industryId of space.relevantIndustries; track industryId) {
            <div class="industry-card active no-break">
              <img [src]="getIndustryImageUrl(industryId)" [alt]="getIndustryName(industryId)" class="industry-image">
              <h5>{{ getIndustryName(industryId) }}</h5>
              <p>{{ getIndustryDescription(industryId) }}</p>
            </div>
          }
        </div>
      </div>
    }

    <!-- Disciplines -->
    @if (space.disciplines.length > 0) {
      <div class="filter-group">
        <h4>{{ 'PDF.COMPANY_PROFILE.DISCIPLINE' | translate }}</h4>
        <div class="filter-buttons">
          @for (d of space.disciplines; track d) {
            <span class="filter-btn active">{{ getDisciplineLabel(d) }}</span>
          }
        </div>
      </div>
    }

    <!-- Materials -->
    @if (space.relevantMaterials.length > 0) {
      <div class="filter-group">
        <h4>{{ 'PDF.COMPANY_PROFILE.MATERIAL' | translate }}</h4>
        <div class="filter-buttons">
          @for (m of space.relevantMaterials; track m) {
            <span class="filter-btn active">{{ getMaterialLabel(m) }}</span>
          }
        </div>
      </div>
    }

    <!-- Recommended products -->
    @if (selectedSolutions.length > 0) {
      <div class="filter-group">
        <h4>{{ 'PDF.COMPANY_PROFILE.RECOMMENDED_PRODUCTS' | translate }}</h4>
        <div class="solutions-grid">
          @for (solution of selectedSolutions; track solution.id) {
            <div class="solution-tile active no-break">
              <div class="tile-border"></div>
              <div class="tile-header">
                <div class="banner-group">
                  <div class="blue-banner">
                    <div class="icon-wrap" [innerHTML]="solutionLogos.get(solution.id)?.icon"></div>
                    <div class="name-wrap" [innerHTML]="solutionLogos.get(solution.id)?.name"></div>
                  </div>
                  <div class="banner-tail">
                    <svg viewBox="0 0 16 48" fill="none" width="16" height="48">
                      <path d="M0 0L16 48H0V0Z" fill="#0066CC"/>
                    </svg>
                  </div>
                </div>
                <div class="category-wrap">
                  <div [innerHTML]="solutionLogos.get(solution.id)?.category"></div>
                </div>
              </div>
              <div class="tile-desc">
                <p>{{ solution.description }}</p>
              </div>
            </div>
          }
        </div>
      </div>
    }

    <!-- Suggested editions -->
    @if (selectedEditions.length > 0) {
      <div class="filter-group">
        <h4>{{ 'EDITIONS.SUGGESTED_TITLE' | translate }}</h4>
        <div class="editions-grid">
          @for (edition of selectedEditions; track edition.id) {
            <div class="editions-card active no-break">
              <div class="title-strip">
                <div class="title-main">
                  <span class="leading-mark">
                    <svg width="7" height="16" viewBox="0 0 7 16" fill="none">
                      <path d="M0 0H1.52174L7 16H5.47826L0 0Z" fill="white"/>
                    </svg>
                  </span>
                  <h4>{{ edition.name }}</h4>
                </div>
                <span class="title-tail">
                  <svg width="11" height="32" viewBox="0 0 11 32" fill="none">
                    <path d="M0 0L11 32H0V0Z" fill="#0066CC"/>
                  </svg>
                </span>
              </div>
              <div class="card-content">
                <div class="details">
                  <div class="term-block">
                    <p class="term-label">{{ edition.termLabel || ('PDF.EDITION.TERM_FALLBACK' | translate) }}</p>
                    <div class="meta-row">
                      <span class="term-value">{{ edition.term }}</span>
                      <span class="price">{{ formatEditionPrice(edition) }}</span>
                    </div>
                  </div>
                  <a class="learn-more" [href]="edition.learnMoreUrl" target="_blank">{{ edition.learnMoreLabel }}</a>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    }
  </section>

  <!-- ══════════════════════════════════════════════
       VALUE ANALYSIS
  ══════════════════════════════════════════════ -->
  <section class="pdf-section page-break-before">
    <h2 class="section-heading">{{ 'DEFINE.VALUE_ANALYSIS.TITLE' | translate }}</h2>

    <p class="sub-label">{{ 'ROI.INPUT.TITLE' | translate }}</p>
    <p class="maturity-label">{{ 'ROI.INPUT.MATURITY_TITLE' | translate }}</p>

    <!-- Maturity slider (CSS-drawn, read-only) -->
    <div class="maturity-track-wrapper">
      <span class="maturity-edge-label">{{ 'ROI.MATURITY_LOW' | translate }}</span>
      <div class="maturity-track">
        <div class="maturity-fill" [style.width.%]="maturityFillPercent"></div>
        <div class="maturity-thumb" [style.left.%]="maturityFillPercent"></div>
      </div>
      <span class="maturity-edge-label">{{ 'ROI.MATURITY_HIGH' | translate }}</span>
    </div>
    <div class="maturity-hint-row">
      <span class="maturity-hint-text">{{ 'PDF.VALUE_ANALYSIS.MATURITY_LOW_HINT' | translate }}</span>
      <span class="maturity-hint-text right">{{ 'PDF.VALUE_ANALYSIS.MATURITY_HIGH_HINT' | translate }}</span>
    </div>

    @if (roiResults) {
      <!-- Percentages of improvement -->
      <p class="sub-label section-gap">{{ 'ROI.INPUT.IMPROVEMENTS_TITLE' | translate }}</p>
      <p class="improvement-hint">{{ 'ROI.INPUT.IMPROVEMENTS_SUBTITLE' | translate }}</p>

      <div class="three-col-row">
        <div class="input-field-box no-break">
          <span class="field-label">{{ 'ROI.INPUT.PRODUCTIVITY_GAIN' | translate }}</span>
          <span class="field-value">{{ roiResults.productionGainPercent | number:'1.0-0' }} %</span>
        </div>
        <div class="input-field-box no-break">
          <span class="field-label">{{ 'ROI.INPUT.REWORK_REDUCTION' | translate }}</span>
          <span class="field-value">{{ roiResults.reworkReductionPercent | number:'1.0-0' }} %</span>
        </div>
        <div class="input-field-box no-break">
          <span class="field-label">{{ 'ROI.INPUT.COORDINATION_SAVINGS' | translate }}</span>
          <span class="field-value">{{ roiResults.coordinationSavingsPercent | number:'1.0-0' }} %</span>
        </div>
      </div>

      <!-- Input values row -->
      <div class="four-col-row section-gap">
        <div class="input-field-box no-break">
          <span class="field-label">{{ 'ROI.HOURLY_RATE' | translate }}</span>
          <span class="field-hint">{{ 'ROI.HOURLY_RATE_SUB' | translate }}</span>
          <span class="field-value">{{ currencySymbol }} {{ space.averageHourlyRate }}</span>
        </div>
        <div class="input-field-box no-break">
          <span class="field-label">{{ 'ROI.TEAM_SIZE' | translate }}</span>
          <span class="field-hint">{{ 'ROI.TEAM_SIZE_SUB' | translate }}</span>
          <span class="field-value">{{ space.teamSize }}</span>
        </div>
        <div class="input-field-box no-break">
          <span class="field-label">{{ 'ROI.PROJECTS_YEAR' | translate }}</span>
          <span class="field-hint">{{ 'ROI.PROJECTS_SUB' | translate }}</span>
          <span class="field-value">{{ space.projectsPerYear }}</span>
        </div>
        <div class="input-field-box no-break">
          <span class="field-label">{{ 'ROI.LICENSE_COST' | translate }}</span>
          <span class="field-hint">{{ 'ROI.LICENSE_SUB' | translate }}</span>
          <span class="field-value">{{ currencySymbol }} {{ space.averageLicenceCost }}</span>
        </div>
      </div>

      <!-- Revenue of Investment -->
      <p class="sub-label section-gap">{{ 'PDF.VALUE_ANALYSIS.ROI_TITLE' | translate }}</p>
      <div class="roi-container">
        <p class="roi-disclaimer">{{ 'PDF.VALUE_ANALYSIS.ROI_DISCLAIMER' | translate }}</p>
        <div class="metric-row">
          <div class="metric-card no-break">
            <div class="metric-top">
              <span class="metric-label">{{ 'ROI.IMPACT.ANNUAL_SAVINGS' | translate }}</span>
              <span class="metric-hint">{{ 'ROI.IMPACT.ANNUAL_SAVINGS_DESC' | translate }}</span>
            </div>
            <strong class="metric-value green">{{ currencySymbol }} {{ formatCurrency(roiResults.estimatedAnnualSavings) }}</strong>
          </div>
          <div class="metric-card no-break">
            <div class="metric-top">
              <span class="metric-label">{{ 'ROI.IMPACT.PRODUCTIVITY_GAIN' | translate }}</span>
              <span class="metric-hint">{{ 'ROI.IMPACT.PRODUCTIVITY_GAIN_DESC' | translate }}</span>
            </div>
            <strong class="metric-value blue">{{ roiResults.productivityGain | number:'1.0-1' }}%</strong>
          </div>
          <div class="metric-card no-break">
            <div class="metric-top">
              <span class="metric-label">{{ 'ROI.IMPACT.PAYBACK_PERIOD' | translate }}</span>
              <span class="metric-hint">{{ 'ROI.IMPACT.PAYBACK_PERIOD_DESC' | translate }}</span>
            </div>
            <strong class="metric-value blue">{{ roiResults.paybackPeriodMonths | number:'1.0-1' }} {{ 'ROI.IMPACT.MONTHS' | translate }}</strong>
          </div>
          <div class="metric-card no-break">
            <div class="metric-top">
              <span class="metric-label">{{ 'ROI.IMPACT.EXTRA_CAPACITY' | translate }}</span>
              <span class="metric-hint">{{ 'ROI.IMPACT.EXTRA_CAPACITY_DESC' | translate }}</span>
            </div>
            <strong class="metric-value blue">{{ roiResults.extraProjectCapacity | number:'1.0-1' }} {{ 'ROI.IMPACT.PROJECTS' | translate }}</strong>
          </div>
        </div>
      </div>
    }
  </section>

  @if (hasDemos || hasUseCases) {
  <!-- ══════════════════════════════════════════════
       DEMO AND PROOF
  ══════════════════════════════════════════════ -->
  <section class="pdf-section page-break-before">
    <h2 class="section-heading">{{ 'DEFINE.DEMO_AND_PROOFS.TITLE' | translate }}</h2>

    <p class="sub-label">{{ 'PDF.DEMO_AND_PROOF.COLLECTION' | translate }}</p>
    <div class="demo-grid">
      @for (item of demoItems; track item.name) {
        <div class="demo-cell no-break">
          <p class="demo-name">{{ item.name }}</p>
          @if (item.description) {
            <p class="demo-desc">{{ item.description }}</p>
          }
          <a class="demo-link" [href]="item.url" target="_blank">{{ item.url }}</a>
        </div>
      }
      @for (uc of useCases; track uc.id) {
        <div class="demo-cell no-break">
          <p class="demo-name">{{ uc.title }}</p>
          <p class="demo-desc">{{ uc.description }}</p>
          <a class="demo-link" [href]="uc.articleUrl" target="_blank">{{ uc.articleUrl }}</a>
        </div>
      }
    </div>
  </section>
  }

</div>
